import { useCallback, useEffect, useState } from "react";

import { isEmpty, isNil } from "ramda";
import { useSearchParams } from "react-router-dom";

import useDebounce from "./useDebounce";

const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const useSearchAndPaginate = (
  fetchData,
  callbackFunctions = {
    all: () => {},
    search: () => {},
    page: () => {},
    pageSize: () => {},
  },
  initialCurrentPage = DEFAULT_CURRENT_PAGE,
  initialPageSize = DEFAULT_PAGE_SIZE
) => {
  const [initialisedSearch, setInitialisedSearch] = useState(false);
  const [initialisedPageSize, setInitialisedPageSize] = useState(false);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState();
  const [pageSize, setPageSize] = useState();

  const debouncedSearchTerm = useDebounce(search, 500);
  const [urlParams, setUrlParams] = useSearchParams();

  /* eslint-disable */
  const noop = () => {};
  const memorizedFn = {
    all: callbackFunctions.all ? useCallback(callbackFunctions.all, []) : noop,
    search: callbackFunctions.search
      ? useCallback(callbackFunctions.search, [])
      : noop,
    page: callbackFunctions.page
      ? useCallback(callbackFunctions.page, [])
      : noop,
    pageSize: callbackFunctions.pageSize
      ? useCallback(callbackFunctions.pageSize, [])
      : noop,
  };
  /* eslint-enable */

  useEffect(() => {
    const searchParam = urlParams.get("search");
    setSearch(searchParam || "");

    const pageSizeParam =
      urlParams.get("per_page") && parseInt(urlParams.get("per_page"));
    setPageSize(Math.max(pageSizeParam, initialPageSize));

    const currentPageParam =
      urlParams.get("page") && parseInt(urlParams.get("page"));
    setCurrentPage(Math.max(currentPageParam, initialCurrentPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCurrentPage, initialPageSize]);

  useEffect(() => {
    if (isNil(pageSize)) return;

    memorizedFn.all();
    memorizedFn.pageSize();

    const params = new URLSearchParams(window.location.search);
    if (pageSize <= initialPageSize) params.delete("per_page");
    else params.set("per_page", pageSize);
    setUrlParams(params);

    if (initialisedSearch && initialisedPageSize && currentPage !== 1) {
      setCurrentPage(1);
    } else if (initialisedSearch && initialisedPageSize) fetchData();

    setInitialisedPageSize(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, fetchData]);

  useEffect(() => {
    if (isNil(debouncedSearchTerm)) return;

    memorizedFn.all();
    memorizedFn.search();

    const params = new URLSearchParams(window.location.search);
    if (isEmpty(debouncedSearchTerm)) params.delete("search");
    else params.set("search", debouncedSearchTerm);
    setUrlParams(params);

    if (initialisedSearch && initialisedPageSize && currentPage !== 1) {
      setCurrentPage(1);
    } else if (initialisedSearch && initialisedPageSize) fetchData();

    setInitialisedSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, fetchData]);

  useEffect(() => {
    if (isNil(currentPage)) return;

    memorizedFn.all();
    memorizedFn.page();

    const params = new URLSearchParams(window.location.search);
    if (currentPage === 1) params.delete("page");
    else params.set("page", currentPage);
    setUrlParams(params);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, fetchData]);

  return {
    search,
    currentPage,
    pageSize,
    setSearch,
    setCurrentPage,
    setPageSize,
    debouncedSearchTerm,
  };
};

export default useSearchAndPaginate;
